import { isForbiddenError, isSuccess } from '@/context/services/type';
import { isCommonError, isObjectRecord } from '@/libs/typeguard/isError';
import {
  useGetInvoiceReportSampleImportCsvHook,
  GetInvoiceReportSampleImportCsvParams,
} from 'ap-openapi';
import { useCallback } from 'react';

export type { GetInvoiceReportSampleImportCsvParams };

export type SampleCsvResult = {
  blob: Blob;
  fileName: string;
};

async function extractBlobMessages(data: unknown): Promise<string> {
  if (!(data instanceof Blob)) return '';
  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (
      isObjectRecord(parsed) &&
      isCommonError(parsed) &&
      Array.isArray(parsed.messages)
    ) {
      return parsed.messages.join('\n');
    }
  } catch {
    // non-JSON blob
  }
  return '';
}

function parseFileName(headers: Record<string, unknown>): string {
  const raw = headers['content-disposition'];
  const disposition = typeof raw === 'string' ? raw : '';
  const match =
    disposition.match(/filename\*=UTF-8''([^;\n]+)/i) ??
    disposition.match(/filename=["']?([^"';\n]+)["']?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : 'sample.csv';
}

export const useDownloadInvoiceReportSampleCsv = () => {
  const fetchCsv = useGetInvoiceReportSampleImportCsvHook();

  return useCallback(
    async (
      params: GetInvoiceReportSampleImportCsvParams
    ): Promise<SampleCsvResult> => {
      const response = await fetchCsv(params);

      if (isSuccess(response.status)) {
        return {
          blob: response.data,
          fileName: parseFileName(response.headers),
        };
      }

      if (isForbiddenError(response.status)) {
        const message = await extractBlobMessages(response.data);
        throw new Error(message);
      }

      throw new Error('');
    },
    [fetchCsv]
  );
};
